import { useForm } from 'react-hook-form';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Button from '@/components/homepage/Button';
import Input, { InputType } from '@/components/Form/Input';
import Title from '@/components/Title/Title';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId } from '@/utils/builderUtils';
import RichText from '@/components/RichText/RichText';
import { sha256 } from '@/utils/utils';
import useMD5HashEmail from '@/hooks/useMD5HashEmail';

const LOOKUP_ERROR_MESSAGE =
  "We couldn't find an order matching that order number and email address. Please check and try again.";

interface ITrackOrderForm {
  submitLabel?: string;
  title: string;
  description: string;
  cta: {
    variant: 'primary' | 'secondary';
    color: 'light' | 'dark';
    label: string;
  };
  anchorLinkID?: string;
}

type TrackOrderFormValues = {
  order: string;
  email: string;
};

export default function TrackOrderForm({ title, description, cta, anchorLinkID }: ITrackOrderForm) {
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const inputs: InputType[] = [
    {
      name: 'order',
      id: 'order',
      label: 'Order Number',
      fullWidth: true,
      type: 'text',
      registerObject: {
        required: true,
        validate: {
          isValid: (v: string) =>
            /^\d{6,}[A-Za-z0-9]*$/.test(String(v || '').trim()) || 'Please enter a valid order number.',
        },
      },
    },
    {
      name: 'email',
      id: 'email',
      label: 'Email',
      fullWidth: true,
      type: 'email',
      registerObject: {
        required: true,
        validate: {
          isValid: (v: string) =>
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(String(v || '').trim()) || 'Invalid Email Address.',
        },
      },
    },
  ];

  const {
    register,
    getValues,
    setFocus,
    trigger,
    formState: { errors },
  } = useForm<TrackOrderFormValues>();

  const getFirstError = useCallback(
    () =>
      Object.keys(errors).reduce<string | null>((field, a) => (errors[field as keyof typeof errors] ? field : a), null),
    [errors]
  );

  const { hashEmail } = useMD5HashEmail();

  async function submitForm() {
    // Sync guard — setState alone is too late for double-click / Enter while in flight
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLookupError(null);

    try {
      const isFormValid = await trigger();
      const { order, email } = getValues();
      if (!isFormValid) {
        const firstError = getFirstError();
        if (firstError) setFocus(firstError as keyof TrackOrderFormValues);
        return;
      }

      const hashedEmail = await hashEmail(email);
      (window as any).dataLayer?.push({
        userEmail: email,
        userHashedEmail: await sha256(email),
        userPhoneNumber: '',
        userFirstName: '',
        userLastName: '',
        hashed_user_email_md5: hashedEmail,
      });

      const response = await fetch('/api/order-status/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: String(order).trim(),
          email: String(email).trim(),
        }),
      });

      const data = await response.json();
      if (!response.ok || data?.error) {
        setLookupError(data?.error?.message || LOOKUP_ERROR_MESSAGE);
        return;
      }

      const trackingUrl = typeof data?.tracking_url === 'string' ? data.tracking_url.trim() : '';
      if (!trackingUrl) {
        setLookupError(LOOKUP_ERROR_MESSAGE);
        return;
      }

      // Relative /orderstatus/?o=&t= (API strips NS www host for non-prod hosts)
      window.location.href = trackingUrl;
    } catch {
      setLookupError('Failed to load delivery information. Please try again later.');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    const firstError = getFirstError();
    if (firstError) {
      setFocus(firstError as keyof TrackOrderFormValues);
    }
  }, [getFirstError, setFocus]);

  return (
    <section
      className={beTwMerge(
        'tw-flex tw-mx-0 sm:tw-mx-4 lg:tw-mx-8 tw-mt-6 lg:tw-mt-10 tw-mb-14 lg:tw-mb-20 min-[1472px]:tw-mx-auto min-[1472px]:tw-w-full tw-max-w-[1440px]'
      )}
      {...getAnchorId(anchorLinkID)}
    >
      <div className="tw-max-w-[682px] tw-mx-4 sm:tw-w-full min-[1472px]:tw-m-0">
        <Title component="h1" weight="normal" size="extraLarge" alignment="left" className="!tw-mb-2">
          {title}
        </Title>
        <RichText className="tw-font-normal tw-text-left tw-mb-[3.25rem]" content={description} />
        <form
          onSubmit={event => {
            event.preventDefault();
            submitForm();
          }}
        >
          <div className="tw-mb-12 sm:tw-mb-14 tw-grid md:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-11">
            {inputs.map(input => (
              <Fragment key={input.name}>
                {/* @ts-ignore */}
                <Input
                  {...input}
                  register={register as any}
                  name={input.name}
                  id={input.id}
                  className={input.className}
                  error={errors[input.name as keyof TrackOrderFormValues] as any}
                />
              </Fragment>
            ))}
          </div>

          {lookupError && (
            <p className="tw-mt-0 tw-mb-8 tw-text-base tw-font-book tw-text-error" role="alert">
              {lookupError}
            </p>
          )}

          <div className="tw-col-span-full tw-flex tw-justify-left">
            <Button
              className="tw-min-w-[200px]"
              label={isSubmitting ? 'Searching…' : cta?.label}
              color={cta?.color === 'light' ? 'white' : 'dark-green'}
              type={cta?.variant}
              onClick={submitForm}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </section>
  );
}
